import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/helpers/cn';
import * as React from 'react';
const FocusTooltip = ({ children, name, content, position = 'auto', ariaContent, className, isPolite = true, }) => {
    const id = `focus-tooltip-${name}`;
    const tooltipRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const [calculatedPosition, setCalculatedPosition] = React.useState('top');
    const shouldAddAriaDescribedBy = ariaContent !== null;
    // Content for screen readers - use ariaContent if provided and not null, otherwise use content
    const screenReaderContent = ariaContent !== undefined && ariaContent !== null ? ariaContent : content;
    const positionClasses = {
        top: 'bottom-full left-1/2 -translate-x-1/2 -translate-y-1',
        bottom: 'top-full left-1/2 -translate-x-1/2 translate-y-1',
        left: 'right-full top-1/2 -translate-y-1/2 -translate-x-1',
        right: 'left-full top-1/2 -translate-y-1/2 translate-x-1',
    };
    const calculateOptimalPosition = React.useCallback(() => {
        if (!containerRef.current || !tooltipRef.current)
            return 'top';
        const container = containerRef.current;
        const tooltip = tooltipRef.current;
        const containerRect = container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Get tooltip dimensions (temporarily show it to measure)
        const originalClasses = tooltip.className;
        tooltip.className = tooltip.className.replace('opacity-0', 'opacity-100');
        tooltip.style.opacity = '1';
        tooltip.style.pointerEvents = 'auto';
        tooltip.style.visibility = 'visible';
        const tooltipRect = tooltip.getBoundingClientRect();
        // Restore original state
        tooltip.className = originalClasses;
        tooltip.style.opacity = '';
        tooltip.style.pointerEvents = '';
        tooltip.style.visibility = '';
        // Find parent containers with overflow hidden
        const getOverflowParents = (element) => {
            const parents = [];
            let currentParent = element.parentElement;
            while (currentParent && currentParent !== document.body) {
                const styles = window.getComputedStyle(currentParent);
                const overflow = styles.overflow;
                const overflowX = styles.overflowX;
                const overflowY = styles.overflowY;
                if (overflow === 'hidden' || overflowX === 'hidden' || overflowY === 'hidden') {
                    parents.push({
                        element: currentParent,
                        rect: currentParent.getBoundingClientRect(),
                        overflow,
                        overflowX,
                        overflowY,
                    });
                }
                currentParent = currentParent.parentElement;
            }
            return parents;
        };
        const overflowParents = getOverflowParents(container);
        // Calculate available space considering both viewport and overflow parents
        let spaceTop = containerRect.top;
        let spaceBottom = viewportHeight - containerRect.bottom;
        let spaceLeft = containerRect.left;
        let spaceRight = viewportWidth - containerRect.right;
        // Reduce available space based on overflow parents
        overflowParents.forEach((parent) => {
            const { rect, overflowY, overflowX } = parent;
            // Check vertical overflow constraints
            if (overflowY === 'hidden' || parent.overflow === 'hidden') {
                spaceTop = Math.min(spaceTop, containerRect.top - rect.top);
                spaceBottom = Math.min(spaceBottom, rect.bottom - containerRect.bottom);
            }
            // Check horizontal overflow constraints
            if (overflowX === 'hidden' || parent.overflow === 'hidden') {
                spaceLeft = Math.min(spaceLeft, containerRect.left - rect.left);
                spaceRight = Math.min(spaceRight, rect.right - containerRect.right);
            }
        });
        // Required space for tooltip (with some padding)
        const tooltipHeight = tooltipRect.height + 8;
        const tooltipWidth = tooltipRect.width + 8;
        // Check if top position has enough space (preferred position)
        const topHorizontalSpace = Math.min(spaceLeft, spaceRight) * 2; // space on both sides
        const isTopHorizontallyConstrained = topHorizontalSpace < tooltipWidth;
        if (spaceTop >= tooltipHeight && !isTopHorizontallyConstrained) {
            return 'top';
        }
        // If top doesn't work, find the best alternative
        const positions = [
            {
                position: 'bottom',
                space: spaceBottom,
                required: tooltipHeight,
                horizontalSpace: Math.min(spaceLeft, spaceRight) * 2,
                horizontalRequired: tooltipWidth,
                isValid: spaceBottom >= tooltipHeight && Math.min(spaceLeft, spaceRight) * 2 >= tooltipWidth,
            },
            {
                position: 'left',
                space: spaceLeft,
                required: tooltipWidth,
                horizontalSpace: spaceLeft,
                horizontalRequired: tooltipWidth,
                isValid: spaceLeft >= tooltipWidth,
            },
            {
                position: 'right',
                space: spaceRight,
                required: tooltipWidth,
                horizontalSpace: spaceRight,
                horizontalRequired: tooltipWidth,
                isValid: spaceRight >= tooltipWidth,
            },
        ];
        // Filter positions that have enough space in both dimensions
        const validPositions = positions.filter((p) => p.isValid);
        if (validPositions.length > 0) {
            // Return the position with the most space among valid alternatives
            const chosen = validPositions.sort((a, b) => b.space - a.space)[0];
            return chosen.position;
        }
        // Fallback: if no position has enough space, choose the one with most space
        // Include top in fallback comparison
        const allPositions = [{ position: 'top', space: spaceTop, required: tooltipHeight }, ...positions];
        const fallback = allPositions.sort((a, b) => b.space - a.space)[0];
        return fallback.position;
    }, []);
    React.useEffect(() => {
        if (position !== 'auto') {
            setCalculatedPosition(position);
        }
        // For auto position, we'll calculate on hover/focus
    }, [position]);
    const handleMouseEnter = () => {
        if (position === 'auto') {
            const optimalPosition = calculateOptimalPosition();
            setCalculatedPosition(optimalPosition);
        }
    };
    const handleFocus = () => {
        if (position === 'auto') {
            const optimalPosition = calculateOptimalPosition();
            setCalculatedPosition(optimalPosition);
        }
    };
    const finalPosition = position === 'auto' ? calculatedPosition : position;
    const politeContent = isPolite ? (shouldAddAriaDescribedBy ? (_jsx("span", { id: id, className: "sr-only", children: screenReaderContent })) : (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", className: "sr-only", children: screenReaderContent }))) : null;
    return (_jsxs("div", { className: cn('group/tooltip relative inline', className), ref: containerRef, onMouseEnter: handleMouseEnter, onFocus: handleFocus, onKeyDown: (event) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                const container = containerRef.current;
                const activeElement = document.activeElement;
                if (container && activeElement && container.contains(activeElement)) {
                    activeElement.blur();
                    event.stopPropagation();
                    event.preventDefault();
                }
            }
        }, children: [children(shouldAddAriaDescribedBy ? { 'aria-describedby': id } : {}), _jsx("span", { ref: tooltipRef, className: `pointer-events-none absolute ${positionClasses[finalPosition]} z-50 bg-surface-secondary px-0.5 py-0.25 type-body-small-book whitespace-nowrap text-text-body opacity-0 group-focus-within/tooltip:opacity-100 group-hover/tooltip:opacity-100`, "aria-hidden": !shouldAddAriaDescribedBy, children: content }), politeContent] }));
};
export default FocusTooltip;
